<template>
  <div>
    <div class="my-4">
      <date-pager
        v-model="date"
        @update:modelValue="pageClick"
      />
    </div>

    <v-progress-linear v-if="isFetching" indeterminate color="primary" class="my-4" />

    <div 
      class="class-block"
      v-for="cwu in classesWithUsages"
      :key="cwu.class.classId"
    >
      <div class="class-block__header">
        <div class="font-weight-bold"> {{ cwu.class.className }} </div>
        <v-spacer />
        <v-btn
          color="primary"
          variant="text"
          :to="`/school-service/usage/${cwu.class.classId}`"
        >月間の利用確認</v-btn>
      </div>

      <div class="table-container mb-4">
        <div class="usages-headers">
          <div class="usages-header col-name">氏名</div>
          <div
            v-for="service in schoolServices"
            :key="service.uniqueServiceId"
            class="usages-header col-item"
          >
            {{ service.uniqueServiceName }}
          </div>
        </div>
        <div
          v-for="usage in cwu.usages"
          :key="usage.child.childId" 
          class="usages-row"
        >
          <div class="usages-item col-name"> {{ usage.child.lastName }} {{ usage.child.firstName }} </div>
          <div
            v-for="service in usage.services"
            :key="service.service.uniqueServiceId"
            class="usages-item col-item"
            @click="toggleUsage(service, usage.child)"
          >
            <v-icon v-if="service.hasUsed" color="primary" size="x-large">mdi-checkbox-marked</v-icon>
            <v-icon v-else color="#c8c8c8" size="x-large">mdi-checkbox-blank-outline</v-icon>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import DatePager from '@/components/atoms/DatePager.vue';
import LocalDataService from '@/service/LocalDataService';

import dayjs from 'dayjs';
import { NurseryChildResponse, NurseryClassResponse, UniqueServiceMastResponse, UniqueServiceRegistrationDetailResponse, UniqueServiceRegistrationResponse } from 'chaild-api/lib';

import { classModule } from '@/store/dataModules/classModule';
import { schoolServiceUsageModule } from '@/store/viewModules/school-service/schoolServiceUsageModule';
import { schoolServiceModule } from '@/store/dataModules/schoolServiceModule';

interface DataType {
  isFetching: boolean;
  date: string;
}

interface ClassWithUsages {
  class: NurseryClassResponse;
  usages: ChildWithPerUseService[];
}

interface ChildWithPerUseService {
  child: NurseryChildResponse;
  services: PerUseServiceWithUsage[];
}

interface PerUseServiceWithUsage {
  service: UniqueServiceMastResponse;
  hasUsed: boolean;
  registrationId?: string | number | null;
}

export default defineComponent({
  components: {
    DatePager,
  },

  data: (): DataType => ({
    isFetching: false,
    date: dayjs().format('YYYY-MM-DD'),
  }),

  computed: {
    classes() {
      return classModule.classes;
    },
    classesWithUsages(): ClassWithUsages[] {
      return this.classes.map((c) => {
        const usages = schoolServiceUsageModule.usages.filter((u) => {
          const find = u.child.nurseryClasses.find((cls) => cls.classId === c.classId);
          if (find) {
            return true;
          }
          return false;
        }).map((u) => {
          const perUseServicesWithUsage = this.schoolServices.map((service) => {
            const findUsage = u.uniqueServices.find((s) => s.uniqueServiceId === service.uniqueServiceId);
            if (findUsage) {
              const registrationId = findUsage.registrations.length > 0 ? findUsage.registrations[0].registrationId : null;
              return {
                service,
                hasUsed: true,
                registrationId,
              }  
            }
            return {
              service,
              hasUsed: false,
            }
          });
          return {
            child: u.child,
            services: perUseServicesWithUsage
          }
        })
        .filter((u) => {
          if (u.child.quitDate) {
            const now = dayjs(this.date);
            const quitDate = dayjs(u.child.quitDate);

            const diff = now.diff(quitDate);
            if (diff > 0) {
              return false;
            }
          }
          return true;
        });

        return {
          class: c,
          usages,
        };
      });
    },
    schoolServices() {
      // ⭕️ 修正5: 二重に filter していたのを1つにまとめる
      return schoolServiceModule.schoolServices.filter(s => s.isHidden !== 1 && s.serviceType === 'perUse');
    },
    tableWidth(): number {
      // 氏名列(160px) + (サービスクラス幅(120px) * サービス数)
      // ⭕️ 修正6: CSSと合わせて正確な幅を計算
      return 160 + (120 * this.schoolServices.length);
    }
  },

  methods: {
    async pageClick(pageNumber: string) {
      if (pageNumber) sessionStorage.setItem('scoolServiceDailyPage', String(pageNumber));
    },
    async toggleUsage(usage: PerUseServiceWithUsage, child: NurseryChildResponse) {
      if (this.isFetching) {
        return;
      }
      if (!usage.hasUsed) {
        this.isFetching = true;
        await schoolServiceUsageModule.registerUsage({
          childId: child.childId,
          uniqueServiceId: usage.service.uniqueServiceId,
          date: this.date,
        }).catch((err) => {
          console.warn(err)
        });
        this.isFetching = false;
      } else {
        if (usage.registrationId) {
          this.isFetching = true;
          await schoolServiceUsageModule.deleteUsage({
            childId: child.childId,
            registrationId: usage.registrationId,
          });
          this.isFetching = false;
        }
      }
    },
    async listUsages() {
      this.isFetching = true;
      await schoolServiceUsageModule.listUsages({
        date: this.date,
      });
      this.isFetching = false;
    }
  },

  watch: {
    date: {
      handler: function() {
        this.listUsages();
      },
    }
  },

  async mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      this.isFetching = true;
      const scoolServiceDailyPage = sessionStorage.getItem('scoolServiceDailyPage');
      if (scoolServiceDailyPage) this.date = scoolServiceDailyPage;
      await Promise.all([
        schoolServiceUsageModule.listUsages({ date: this.date }),
        schoolServiceModule.listSchoolServices()
      ]);
      this.isFetching = false;
    }
  }
});
</script>

<style lang="scss" scoped>
.class-block__header {
  background-color: #f0f0f0;
  padding: 0 8px;
  margin: 16px 0;
  display: flex;
  align-items: center;
}

.usages-headers {
  display: flex;
  align-items: center;
  height: 45px;
  min-width: max-content;
  border-bottom: solid 1px #e0e0e0;
  position: sticky;
  top: 0;
  background-color: #ffffff;
  z-index: 4;
}

.usages-header {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 1px;
  min-height: 45px;
}

.usages-row {
  display: flex;
  align-items: center;
  min-width: max-content;
  margin-top: 8px;
  height: 40px;

  &:hover {
    background-color: #f5f5f5;
  }
}

.usages-item {
  font-size: 14px;
  width: 120px;
  padding: 8px;
  z-index: 1;

  &.col-item {
    cursor: pointer;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background-color: #eaeaea;
    }
  }
}

.col-name {
  width: 160px;
  min-width: 160px;
  padding: 0 4px;
  position: sticky;
  left: 0; /* start より left の方が安定します */
  background-color: #ffffff;
  z-index: 3;
}

.col-item {
  width: 120px;
  min-width: 120px;
  padding: 8px;
  text-align: center;
  font-size: 10px;
  word-break: break-all;
}

.table-container {
  overflow-x: auto; /* visible から auto に変更 (横にはみ出た分だけスクロール) */
  overflow-y: auto;
  position: relative;
  max-height: 800px;
}
</style>